export const translations = {
  en: {
    nav: {
      solutions: "Solutions",
      framework: "MAX Framework",
      blueprints: "Blueprints",
      ecosystem: "Ecosystem",
      services: "Services",
      insights: "Insights",
      contact: "Contact",
      cta: "Book a Discovery Session",
    },
    hero: {
      eyebrow: "Indonesia's Digital Infrastructure Company™",
      title: "Build Your Digital Operating System™",
      sub: "Transform fragmented operations into intelligent, connected and scalable ecosystems powered by AI, data and automation.",
      primary: "Book a Discovery Session",
      secondary: "Explore Solutions",
      diagramTitle: "Digital Operating System",
      diagramSubtitle: "Reference Architecture",
    },
    philosophy: {
      eyebrow: "Core Philosophy",
      old: "Old World",
      oldText: "People manage systems.",
      new: "New World",
      newText: "Systems empower people.",
      framework: "People → Operations → Data → AI → Insights → Decisions → Outcomes",
      tagline: "From Operations to Intelligence™",
    },
    pillars: {
      eyebrow: "Eight Solution Pillars",
      title: "An Operating System for every layer of the enterprise.",
      sub: "We do not sell software. We build infrastructure for modern organizations.",
      items: [
        { id: "experience", name: "Experience OS™", points: ["Customer journeys", "Feedback & complaints", "Notifications & loyalty", "WhatsApp · AI Concierge"] },
        { id: "workforce", name: "Workforce OS™", points: ["Recruitment & attendance", "Certification & training", "Performance & shifts", "Digital IDs · Knowledge"] },
        { id: "operations", name: "Operations OS™", points: ["Approvals & tasks", "Projects & incidents", "SOPs & automation", "Multi-site control"] },
        { id: "commercial", name: "Commercial OS™", points: ["Parking · retail · hotels", "Merchants & advertising", "Tourism", "Revenue optimization"] },
        { id: "data", name: "Data Intelligence OS™", points: ["Executive dashboards", "Predictions & analytics", "Decision support", "Insights at scale"] },
        { id: "licensing", name: "Licensing OS™", points: ["KBLI workflows", "Licensing & renewals", "Compliance", "Government integration"] },
        { id: "agents", name: "AI Agents™", points: ["Employee AI", "Customer AI", "Knowledge AI", "Operations & Executive AI"] },
        { id: "industry", name: "Industry Solutions™", points: ["Ports · Healthcare", "Education · Hospitality", "Government · Property", "Logistics · Mobility"] },
      ],
    },
    framework: {
      eyebrow: "The MAX Framework™",
      title: "Map. Automate. Expand.",
      sub: "A structured operating model to move organizations from fragmented operations to intelligent ecosystems.",
      steps: ["Map", "Connect", "Automate", "Predict", "Optimize", "Scale"],
      m: { letter: "M", label: "Map Operations", text: "Surface every workflow, system and signal across your organization." },
      a: { letter: "A", label: "Automate Workflows", text: "Replace fragmented manual steps with connected, governed processes." },
      x: { letter: "X", label: "Expand Intelligence", text: "Embed AI and data products into the daily decisions of your teams." },
    },
    blueprints: {
      eyebrow: "Solution Blueprints",
      title: "Reference architectures, not case studies.",
      sub: "Each blueprint outlines how a Digital Operating System can be assembled for a specific operational context.",
      label: "Solution Blueprint",
      items: [
        {
          id: "asdp",
          name: "ASDP Mobility Ecosystem™",
          challenge: "Disconnected passenger journeys across ports, ticketing, vessels and ground transport result in fragmented service and limited operational visibility.",
          architecture: "Experience OS + Workforce OS + Commercial OS + Data Intelligence OS unified under a Passenger Journey backbone.",
          workflow: "Pre-trip → Port arrival → Boarding → Onboard → Disembark → Post-trip feedback. All events streamed into a single operations data layer.",
          outcomes: ["Unified passenger identity", "Real-time port operations visibility", "Commercial revenue optimization", "Predictive passenger flow"],
        },
        {
          id: "talentxid",
          name: "TalentXID Workforce Infrastructure™",
          challenge: "Workforce mobility programs are fragmented across recruiters, training centers, agencies and destination employers — with no shared source of truth.",
          architecture: "Workforce OS + Licensing OS + AI Agents + Knowledge OS connected through a Worker Digital ID layer.",
          workflow: "Sourcing → Certification → Training → Deployment → In-country support → Returnee program.",
          outcomes: ["Verified worker digital identity", "Compliance-grade documentation", "Faster deployment cycles", "Long-term career intelligence"],
        },
        {
          id: "ibc",
          name: "IBC Licensing OS™",
          challenge: "Business licensing in Indonesia spans multiple ministries, KBLI classifications and renewal cycles — driving operational risk for multi-entity groups.",
          architecture: "Licensing OS + Operations OS + Data Intelligence OS with workflow connectors to government systems.",
          workflow: "Entity onboarding → KBLI mapping → Filing → Renewal tracking → Compliance dashboards.",
          outcomes: ["Single licensing source of truth", "Renewal risk alerts", "Audit-ready documentation", "Executive compliance view"],
        },
        {
          id: "healthcare",
          name: "Healthcare Global Mobility™",
          challenge: "Cross-border placement of healthcare professionals requires coordination across credentialing, language, training, regulators and destination employers.",
          architecture: "Workforce OS + Knowledge OS + Experience OS + AI Agents.",
          workflow: "Candidate intake → Credential verification → Language & clinical training → Regulator filing → Placement → Post-placement support.",
          outcomes: ["Credential-grade worker profiles", "Regulator-aligned workflows", "Reduced placement time", "Continuous learning loop"],
        },
      ],
      challenge: "Challenge",
      arch: "Architecture",
      flow: "Workflow",
      out: "Expected Outcomes",
    },
    ecosystem: {
      eyebrow: "Group Ecosystem",
      title: "Strategic Ecosystem Partners",
      sub: "MaxSolution™ operates inside a broader network of execution partners — bringing together infrastructure, workforce and sector expertise.",
      nodes: [
        { name: "MaxSolution™", role: "Digital Infrastructure Engine" },
        { name: "TalentXID™", role: "Workforce Infrastructure Platform" },
        { name: "Golden Port", role: "Maritime & Port Ecosystem Partner" },
        { name: "PT Hadeyama Jaya Perkasa", role: "Licensed Workforce Operations Partner" },
      ],
    },
    featured: {
      eyebrow: "Featured Use Case · Strategic Blueprint",
      title: "Transforming Ports Into Intelligent Ecosystems™",
      sub: "A reference architecture for how a national port operator could connect passengers, workforce, commerce and intelligence into a single operating system.",
      capabilities: [
        "Passenger Experience Hub™",
        "Journey OS™",
        "Workforce OS™",
        "Commercial OS™",
        "Data Intelligence OS™",
        "AI Concierge™",
      ],
    },
    services: {
      eyebrow: "Services",
      title: "End-to-end execution capability.",
      sub: "From boardroom strategy to long-term managed operations — designed for organizations that need both vision and delivery.",
      items: [
        "Strategy",
        "Digital Transformation",
        "AI Transformation",
        "Workflow Automation",
        "System Integration",
        "Knowledge Management",
        "Business Intelligence",
        "Dashboard Development",
        "WhatsApp Automation",
        "Digital Product Development",
        "Implementation",
        "Managed Services",
        "Training",
      ],
    },
    why: {
      eyebrow: "Why MaxSolution™",
      title: "Strategy and execution. Under one roof.",
      items: [
        { t: "Bridge strategy and execution", d: "We translate boardroom intent into production-grade systems." },
        { t: "Focus on operations, not hype", d: "We solve the unglamorous problems that move the needle." },
        { t: "Systems people actually use", d: "We design for adoption — by frontline teams and executives." },
        { t: "AI inside existing workflows", d: "We embed intelligence where the work already happens." },
        { t: "Designed for long-term scale", d: "Architected for multi-year transformation, not pilots." },
      ],
    },
    insights: {
      eyebrow: "Insights",
      title: "Perspectives from the operating layer.",
      sub: "Thought leadership on what it takes to operate intelligent organizations in Indonesia and across the region.",
      items: [
        { tag: "Operating Model", title: "Why Indonesia's next decade depends on operational software, not apps.", read: "8 min read" },
        { tag: "AI", title: "Embedded intelligence: the quiet revolution inside enterprise workflows.", read: "6 min read" },
        { tag: "Workforce", title: "Digital identity as the foundation of cross-border workforce mobility.", read: "7 min read" },
        { tag: "Ports", title: "From terminals to ecosystems: rethinking the port as an operating system.", read: "9 min read" },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's build your Digital Operating System.",
      sub: "Tell us about your organization. We will respond with a structured Discovery Session agenda.",
      name: "Full name",
      org: "Organization",
      email: "Work email",
      role: "Role",
      message: "Briefly describe your operating challenge",
      submit: "Request Discovery Session",
      sent: "Thank you. Our team will reach out within two business days.",
    },
    footer: {
      legal: "PT Max Solution Indonesia",
      tagline: "Building Digital Operating Systems™",
      rights: "All rights reserved.",
      brand: "Brand",
      solutions: "Solutions",
      company: "Company",
      links: {
        about: "About",
        framework: "MAX Framework",
        ecosystem: "Ecosystem",
        services: "Services",
        insights: "Insights",
        contact: "Contact",
      },
    },
  },
  id: {
    nav: {
      solutions: "Solusi",
      framework: "Kerangka MAX",
      blueprints: "Blueprint",
      ecosystem: "Ekosistem",
      services: "Layanan",
      insights: "Wawasan",
      contact: "Kontak",
      cta: "Jadwalkan Discovery Session",
    },
    hero: {
      eyebrow: "Perusahaan Infrastruktur Digital Indonesia™",
      title: "Bangun Digital Operating System™ Anda",
      sub: "Ubah operasi yang terfragmentasi menjadi ekosistem yang cerdas, terhubung, dan skalabel — didukung AI, data, dan otomasi.",
      primary: "Jadwalkan Discovery Session",
      secondary: "Jelajahi Solusi",
      diagramTitle: "Digital Operating System",
      diagramSubtitle: "Arsitektur Referensi",
    },
    philosophy: {
      eyebrow: "Filosofi Inti",
      old: "Dunia Lama",
      oldText: "Manusia mengelola sistem.",
      new: "Dunia Baru",
      newText: "Sistem memberdayakan manusia.",
      framework: "Manusia → Operasi → Data → AI → Wawasan → Keputusan → Hasil",
      tagline: "From Operations to Intelligence™",
    },
    pillars: {
      eyebrow: "Delapan Pilar Solusi",
      title: "Sebuah Operating System untuk setiap lapisan organisasi.",
      sub: "Kami tidak menjual software. Kami membangun infrastruktur untuk organisasi modern.",
      items: [
        { id: "experience", name: "Experience OS™", points: ["Customer journey", "Umpan balik & komplain", "Notifikasi & loyalitas", "WhatsApp · AI Concierge"] },
        { id: "workforce", name: "Workforce OS™", points: ["Rekrutmen & absensi", "Sertifikasi & pelatihan", "Performa & shift", "Digital ID · Knowledge"] },
        { id: "operations", name: "Operations OS™", points: ["Approval & tugas", "Proyek & insiden", "SOP & otomasi", "Kendali multi-site"] },
        { id: "commercial", name: "Commercial OS™", points: ["Parkir · retail · hotel", "Merchant & advertising", "Pariwisata", "Optimasi pendapatan"] },
        { id: "data", name: "Data Intelligence OS™", points: ["Dashboard eksekutif", "Prediksi & analitik", "Dukungan keputusan", "Wawasan skala besar"] },
        { id: "licensing", name: "Licensing OS™", points: ["Alur KBLI", "Perizinan & perpanjangan", "Kepatuhan", "Integrasi pemerintah"] },
        { id: "agents", name: "AI Agents™", points: ["AI untuk karyawan", "AI untuk pelanggan", "Knowledge AI", "AI Operasi & Eksekutif"] },
        { id: "industry", name: "Industry Solutions™", points: ["Pelabuhan · Kesehatan", "Pendidikan · Hospitality", "Pemerintahan · Properti", "Logistik · Mobilitas"] },
      ],
    },
    framework: {
      eyebrow: "Kerangka MAX™",
      title: "Map. Automate. Expand.",
      sub: "Model operasi terstruktur untuk memindahkan organisasi dari operasi terfragmentasi menuju ekosistem yang cerdas.",
      steps: ["Map", "Connect", "Automate", "Predict", "Optimize", "Scale"],
      m: { letter: "M", label: "Map Operations", text: "Petakan setiap alur kerja, sistem dan sinyal di seluruh organisasi Anda." },
      a: { letter: "A", label: "Automate Workflows", text: "Ganti langkah manual yang terfragmentasi dengan proses yang terhubung dan tertata." },
      x: { letter: "X", label: "Expand Intelligence", text: "Tanamkan AI dan produk data ke dalam keputusan harian tim Anda." },
    },
    blueprints: {
      eyebrow: "Solution Blueprint",
      title: "Arsitektur referensi, bukan studi kasus.",
      sub: "Setiap blueprint menjelaskan bagaimana sebuah Digital Operating System dapat dirakit untuk konteks operasi tertentu.",
      label: "Solution Blueprint",
      items: [
        {
          id: "asdp",
          name: "ASDP Mobility Ecosystem™",
          challenge: "Perjalanan penumpang yang terputus antara pelabuhan, tiket, kapal dan transportasi darat menghasilkan layanan terfragmentasi dan visibilitas operasi yang terbatas.",
          architecture: "Experience OS + Workforce OS + Commercial OS + Data Intelligence OS yang disatukan oleh backbone Passenger Journey.",
          workflow: "Pra-perjalanan → Tiba di pelabuhan → Boarding → Di atas kapal → Disembark → Umpan balik pasca-perjalanan.",
          outcomes: ["Identitas penumpang yang terpadu", "Visibilitas operasi pelabuhan real-time", "Optimasi pendapatan komersial", "Prediksi arus penumpang"],
        },
        {
          id: "talentxid",
          name: "TalentXID Workforce Infrastructure™",
          challenge: "Program mobilitas tenaga kerja terfragmentasi antar perekrut, lembaga pelatihan, agen dan pemberi kerja tujuan — tanpa satu sumber kebenaran.",
          architecture: "Workforce OS + Licensing OS + AI Agents + Knowledge OS terhubung melalui lapisan Worker Digital ID.",
          workflow: "Sourcing → Sertifikasi → Pelatihan → Penempatan → Dukungan di negara tujuan → Program kepulangan.",
          outcomes: ["Identitas digital pekerja yang terverifikasi", "Dokumentasi setara kepatuhan", "Siklus penempatan lebih cepat", "Intelijen karir jangka panjang"],
        },
        {
          id: "ibc",
          name: "IBC Licensing OS™",
          challenge: "Perizinan usaha di Indonesia mencakup berbagai kementerian, klasifikasi KBLI dan siklus perpanjangan — meningkatkan risiko operasi untuk grup multi-entitas.",
          architecture: "Licensing OS + Operations OS + Data Intelligence OS dengan konektor alur kerja ke sistem pemerintah.",
          workflow: "Onboarding entitas → Pemetaan KBLI → Pengajuan → Pelacakan perpanjangan → Dashboard kepatuhan.",
          outcomes: ["Satu sumber kebenaran perizinan", "Peringatan risiko perpanjangan", "Dokumentasi siap audit", "Pandangan kepatuhan eksekutif"],
        },
        {
          id: "healthcare",
          name: "Healthcare Global Mobility™",
          challenge: "Penempatan lintas negara tenaga kesehatan memerlukan koordinasi antara kredensial, bahasa, pelatihan, regulator dan pemberi kerja tujuan.",
          architecture: "Workforce OS + Knowledge OS + Experience OS + AI Agents.",
          workflow: "Intake kandidat → Verifikasi kredensial → Pelatihan bahasa & klinis → Pengajuan regulator → Penempatan → Dukungan pasca penempatan.",
          outcomes: ["Profil pekerja setara kredensial", "Alur kerja selaras regulator", "Waktu penempatan lebih singkat", "Lingkar pembelajaran berkelanjutan"],
        },
      ],
      challenge: "Tantangan",
      arch: "Arsitektur",
      flow: "Alur Kerja",
      out: "Hasil yang Diharapkan",
    },
    ecosystem: {
      eyebrow: "Ekosistem Grup",
      title: "Mitra Ekosistem Strategis",
      sub: "MaxSolution™ beroperasi di dalam jaringan mitra eksekusi yang lebih luas — menyatukan infrastruktur, tenaga kerja dan keahlian sektor.",
      nodes: [
        { name: "MaxSolution™", role: "Mesin Infrastruktur Digital" },
        { name: "TalentXID™", role: "Platform Infrastruktur Tenaga Kerja" },
        { name: "Golden Port", role: "Mitra Ekosistem Maritim & Pelabuhan" },
        { name: "PT Hadeyama Jaya Perkasa", role: "Mitra Operasi Tenaga Kerja Berlisensi" },
      ],
    },
    featured: {
      eyebrow: "Featured Use Case · Strategic Blueprint",
      title: "Mengubah Pelabuhan Menjadi Ekosistem Cerdas™",
      sub: "Arsitektur referensi tentang bagaimana operator pelabuhan nasional dapat menghubungkan penumpang, tenaga kerja, komersial dan intelijen ke dalam satu operating system.",
      capabilities: [
        "Passenger Experience Hub™",
        "Journey OS™",
        "Workforce OS™",
        "Commercial OS™",
        "Data Intelligence OS™",
        "AI Concierge™",
      ],
    },
    services: {
      eyebrow: "Layanan",
      title: "Kapabilitas eksekusi end-to-end.",
      sub: "Dari strategi tingkat direksi hingga operasi terkelola jangka panjang — dirancang untuk organisasi yang membutuhkan visi dan eksekusi.",
      items: [
        "Strategi",
        "Transformasi Digital",
        "Transformasi AI",
        "Otomasi Alur Kerja",
        "Integrasi Sistem",
        "Knowledge Management",
        "Business Intelligence",
        "Pengembangan Dashboard",
        "Otomasi WhatsApp",
        "Pengembangan Produk Digital",
        "Implementasi",
        "Managed Services",
        "Pelatihan",
      ],
    },
    why: {
      eyebrow: "Mengapa MaxSolution™",
      title: "Strategi dan eksekusi. Di bawah satu atap.",
      items: [
        { t: "Menjembatani strategi dan eksekusi", d: "Kami menerjemahkan niat direksi menjadi sistem siap produksi." },
        { t: "Fokus pada operasi, bukan hype", d: "Kami menyelesaikan masalah operasi yang benar-benar berdampak." },
        { t: "Sistem yang benar-benar dipakai", d: "Kami merancang untuk adopsi — oleh tim lapangan dan eksekutif." },
        { t: "AI di dalam alur kerja yang sudah ada", d: "Kami menanamkan intelijen di tempat pekerjaan benar-benar terjadi." },
        { t: "Dirancang untuk skala jangka panjang", d: "Arsitektur untuk transformasi multi-tahun, bukan pilot." },
      ],
    },
    insights: {
      eyebrow: "Wawasan",
      title: "Perspektif dari lapisan operasi.",
      sub: "Pemikiran tentang apa yang dibutuhkan untuk menjalankan organisasi cerdas di Indonesia dan kawasan.",
      items: [
        { tag: "Model Operasi", title: "Mengapa dekade berikutnya Indonesia bergantung pada software operasi, bukan aplikasi.", read: "8 menit baca" },
        { tag: "AI", title: "Embedded intelligence: revolusi senyap di dalam alur kerja perusahaan.", read: "6 menit baca" },
        { tag: "Tenaga Kerja", title: "Identitas digital sebagai fondasi mobilitas tenaga kerja lintas negara.", read: "7 menit baca" },
        { tag: "Pelabuhan", title: "Dari terminal menjadi ekosistem: memikirkan ulang pelabuhan sebagai operating system.", read: "9 menit baca" },
      ],
    },
    contact: {
      eyebrow: "Kontak",
      title: "Mari bangun Digital Operating System Anda.",
      sub: "Ceritakan tentang organisasi Anda. Kami akan merespons dengan agenda Discovery Session yang terstruktur.",
      name: "Nama lengkap",
      org: "Organisasi",
      email: "Email kerja",
      role: "Jabatan",
      message: "Jelaskan tantangan operasi Anda secara singkat",
      submit: "Minta Discovery Session",
      sent: "Terima kasih. Tim kami akan menghubungi Anda dalam dua hari kerja.",
    },
    footer: {
      legal: "PT Max Solution Indonesia",
      tagline: "Building Digital Operating Systems™",
      rights: "Hak cipta dilindungi.",
      brand: "Brand",
      solutions: "Solusi",
      company: "Perusahaan",
      links: {
        about: "Tentang",
        framework: "Kerangka MAX",
        ecosystem: "Ekosistem",
        services: "Layanan",
        insights: "Wawasan",
        contact: "Kontak",
      },
    },
  },
};
